import {injectable} from 'inversify';

export interface ICurrencyData {
	currency: string;
	decimalPlaces: number;
	decimalSeparator: string;
	thousandSeparator: string;
	currencyPrefix: string;
	currencySuffix: string;
	currencySign: string;
}

export interface IISOCodeFormat {
	[IsoCode: string]: string;
}

@injectable()
export class CurrencyFormatter {
	public static CurrenciesDisplayFormat: IISOCodeFormat = {
		AED: '#,###.##',
		ALL: '#,###.##',
		AMD: '#,###.##',
		AOA: 'Kz#,###',
		ARS: '$#,###.##',
		ATS: 'S #.###,##',
		AUD: '$#,###.##',
		BAM: '#,###.##',
		BEF: '#.###,##',
		BGN: '#,###.##',
		BIF: 'FBu#,###.##',
		BOB: '#,###.##',
		BRL: '#,###.##',
		BWP: 'P#,###',
		CAD: '$#,###.##',
		CDF: 'FC#,###',
		CHF: "SFr. #'###.##",
		CLP: '#,###.##',
		CNY: '¥#,###.##',
		COP: '$ #.###,##',
		CRC: '#,###',
		CVE: '$#,###',
		CZK: '# ###,## K',
		DEM: '#.###,## DM',
		DJF: 'Fdj#,###',
		DKK: 'kr #.###,##',
		DOP: '#,###.##',
		DZD: 'DA#,###',
		EEK: '# ###.## kr',
		EGP: 'E?#,###',
		ERN: 'Nfk#,###',
		ESP: '#.###,## Pts',
		ETB: 'Br#,###',
		EUR: '€ #.###,##',
		FIM: '# ###,## mk',
		FRF: '# ###,##',
		GBP: '£#,###.##',
		GEL: '#,###.##',
		GHC: '#,###.##',
		GHS: '₡ #,###.##',
		GMD: 'D#,###',
		GNF: 'FG#,###',
		GRD: '#.###,##',
		HKD: 'HK$#,###.##',
		HNL: '#,###.##',
		HRK: '#,###.##',
		HUF: '# ###,## Ft',
		IDR: '#,###.##',
		IQD: '#,###.###د.ع',
		IEP: '£#,###.##',
		ILS: '₪ #,###.##',
		INR: '#,###.##',
		IRR: '﷼#,###.##',
		ISK: '#.###,## kr.',
		ITL: '#.###,##',
		JPY: '¥#,###',
		KES: 'KSh#,###',
		KGS: '#,###.##',
		KHR: '#,###',
		KMF: 'CF#,###',
		KRW: '# ###.##',
		KZT: '#,###.##',
		LAK: '#,###',
		LRD: 'L$#,###',
		LSL: 'L#,###',
		LTL: '#,###.##',
		LVL: '#,###.##',
		LYD: 'LD#,###',
		MAD: '#,###.##',
		MDL: '#,###.##',
		MNT: '₮#,###.##',
		MMK: 'K#,###',
		MKD: 'ден#,###.##',
		MOP: '#,###.##',
		MRO: 'UM#,###',
		MUR: '#,###',
		MWK: 'MK#,###',
		MXN: '$#,###.##',
		MYR: 'RM#,###.##',
		MZN: 'MT#,###',
		NAD: '#,###.##',
		NGN: '#,###.##',
		NLG: 'ƒ #.###,##',
		NOK: 'kr # ###,##',
		NONE: '#,###.##',
		NZD: '$#,###.##',
		PEN: 'S/. #,###.##',
		PHP: 'Php#,###.##',
		PKR: 'Rs#,###.##',
		PLN: '# ###,## z',
		PTE: '#.###,## $',
		PYG: '#,###.##',
		RON: '#,###.##',
		RSD: '#,###.##',
		RUB: '# ###,##.',
		RWF: 'FRw#,###',
		SCR: 'SR#,###.##',
		SDG: '#,###.##',
		SEK: '#.###,## kr',
		SGD: '$#,###.##',
		SKK: '# ###,## Sk',
		SZL: 'L#,###',
		THB: '#,###.##',
		TND: '#,###.###د.ت',
		TRL: '#,###.##',
		TRY: '#,###.##',
		TWD: 'NT$#,###.##',
		TZS: 'TSh #,###.##',
		UAH: '#,###.##',
		UGX: '#,###.##',
		USD: '$#,###.##',
		UYU: '#,###.##',
		VEF: '#,###.##',
		VND: '#,###.##',
		XAF: '#,###.##',
		XCD: '#,###.##',
		XOF: '#,###.##',
		ZAR: 'R #,###.##',
		ZMW: 'K#,###.##',
	};
	protected _currency: string;
	protected _currencySign: string;
	protected _currencyDecimals: number;
	protected _currencyPrefix: string;
	protected _currencySuffix: string;
	protected _decimalSeparator: string;
	protected _thousandSeparator: string;

	constructor() {
		// Temp
		this.setCurrencyData(this.parseCurrencyFormatting('USD', CurrencyFormatter.CurrenciesDisplayFormat.USD));
	}

	public formatCurrencyFromCents(cents: number): string {
		return this.formatCurrency(cents / 100);
	}

	public setupCurrencyFromISO(isoCode: string): void {
		if (CurrencyFormatter.CurrenciesDisplayFormat[isoCode]) {
			this.setCurrencyData(this.parseCurrencyFormatting(isoCode, CurrencyFormatter.CurrenciesDisplayFormat[isoCode]));
		}
	}

	public setCurrencyData(currencyData: ICurrencyData): void {
		if (currencyData !== null) {
			this.currency = currencyData.currency;
			this.currencySign = currencyData.currencySign;
			this.currencyDecimals = currencyData.decimalPlaces;
			this.currencyPrefix = currencyData.currencyPrefix;
			this.currencySuffix = currencyData.currencySuffix;
			this.decimalSeparator = currencyData.decimalSeparator;
			this.thousandSeparator = currencyData.thousandSeparator;
		}
	}

	public formatNumber(valueAmount: number | string, includeDecimals: boolean = true): string {
		const currency: ICurrencyData = {
			currency: this.currency,
			decimalPlaces: this.currencyDecimals,
			thousandSeparator: this.thousandSeparator,
			decimalSeparator: this.decimalSeparator,
			currencyPrefix: this.currencyPrefix,
			currencySuffix: this.currencySuffix,
			currencySign: this.currencySign,
		};

		valueAmount = (valueAmount + '').replace(/[^0-9+\-Ee.]/g, '');

		const n = !isFinite(+valueAmount) ? 0 : +valueAmount;
		const prec = !isFinite(+currency.decimalPlaces) ? 0 : Math.abs(currency.decimalPlaces);
		const sep = typeof currency.thousandSeparator === 'undefined' ? ',' : currency.thousandSeparator;
		const dec = typeof currency.decimalSeparator === 'undefined' ? '.' : currency.decimalSeparator;

		let s: string[] = [''];

		// tslint:disable-next-line:no-shadowed-variable
		const toFixedFix = function(n: number, prec: number): string {
			const k = Math.pow(10, prec);

			return '' + (Math.round(n * k) / k).toFixed(prec);
		};

		s = (prec ? toFixedFix(n, prec) : '' + Math.round(n)).split('.');

		if (s[0].length > 3) {
			s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
		}

		if ((s[1] || '').length < prec) {
			s[1] = s[1] || '';
			s[1] += new Array(prec - s[1].length + 1).join('0');
		}

		return includeDecimals ? s.join(dec) : s[0];
	}

	public formatCurrencyApi(value: number): string {
		return this.formatCurrency(value, true);
	}

	public formatCurrency(valueAmount: number | string, includeCurrencySign: boolean = true): string {
		const currency: ICurrencyData = {
			currency: this.currency,
			decimalPlaces: this.currencyDecimals,
			thousandSeparator: this.thousandSeparator,
			decimalSeparator: this.decimalSeparator,
			currencyPrefix: this.currencyPrefix,
			currencySuffix: this.currencySuffix,
			currencySign: this.currencySign,
		};

		valueAmount = (valueAmount + '').replace(/[^0-9+\-Ee.]/g, '');

		const n = !isFinite(+valueAmount) ? 0 : +valueAmount;
		const prec = !isFinite(+currency.decimalPlaces) ? 0 : Math.abs(currency.decimalPlaces);
		const sep = typeof currency.thousandSeparator === 'undefined' ? ',' : currency.thousandSeparator;
		const dec = typeof currency.decimalSeparator === 'undefined' ? '.' : currency.decimalSeparator;

		let s: string[] = [''];

		// tslint:disable-next-line:no-shadowed-variable
		const toFixedFix = function(n: number, prec: number): string {
			const k = Math.pow(10, prec);

			return '' + (Math.round(n * k) / k).toFixed(prec);
		};

		s = (prec ? toFixedFix(n, prec) : '' + Math.round(n)).split('.');

		if (s[0].length > 3) {
			s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
		}

		if ((s[1] || '').length < prec) {
			s[1] = s[1] || '';
			s[1] += new Array(prec - s[1].length + 1).join('0');
		}

		return includeCurrencySign ? currency.currencyPrefix + s.join(dec) + currency.currencySuffix : s.join(dec);
	}

	protected parseCurrencyFormatting(isoCode: string, displayFormat: string): ICurrencyData {
		/**
		 * Should be 4 capturing groups.
		 * 1: Prefix symbols
		 * 2: Thousand separator.
		 * 3: Decimal separator.
		 * 4: Decimal places.
		 * 5: Suffix symbols
		 */
		// const regexMatch = /(.*)?(?:#)(['|,|.|\s])(?:###)([,|.|\s])?([#]*)?(.*)?/;
		const regexMatch = /([^#]*)?(?:#)(['|,|.|\s])(?:###)([,|.|\s])?([#]*)?(.*)?/;
		const dataObject = displayFormat.match(regexMatch);

		const currency = isoCode;
		const decimalPlaces = dataObject[4] ? dataObject[4].length : 0;
		const thousandSeparator = dataObject[2] ? dataObject[2] : ',';
		const decimalSeparator = dataObject[3] ? dataObject[3] : '.';
		const currencyPrefix = dataObject[1] ? dataObject[1] : '';
		const currencySuffix = dataObject[5] ? dataObject[5] : '';
		const currencySign = dataObject[1] ? currencyPrefix : currencySuffix;

		const currencyData: ICurrencyData = {
			currency,
			currencySign,
			decimalPlaces,
			thousandSeparator,
			decimalSeparator,
			currencyPrefix,
			currencySuffix,
		};

		return currencyData;
	}

	public set currencySign(value: string) {
		this._currencySign = value;
	}

	public get currencySign(): string {
		return this._currencySign;
	}

	public set currencyDecimals(value: number) {
		this._currencyDecimals = value;
	}

	public get currencyDecimals(): number {
		return this._currencyDecimals;
	}

	public set currencyPrefix(value: string) {
		this._currencyPrefix = value;
	}

	public get currencyPrefix(): string {
		return this._currencyPrefix;
	}

	public set currencySuffix(value: string) {
		this._currencySuffix = value;
	}

	public get currencySuffix(): string {
		return this._currencySuffix;
	}

	public set thousandSeparator(value: string) {
		this._thousandSeparator = value;
	}

	public get thousandSeparator(): string {
		return this._thousandSeparator;
	}

	public set decimalSeparator(value: string) {
		this._decimalSeparator = value;
	}

	public get decimalSeparator(): string {
		return this._decimalSeparator;
	}

	public set currency(value: string) {
		this._currency = value;
	}

	public get currency(): string {
		return this._currency;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/utils/CurrencyFormatter.ts